import React, { useEffect, useState, useContext, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Redirect, Route } from 'react-router-dom';
import Layout from './components/Layout';
import { getStoryblokAPI, initEditor } from './utils/storyblok-bridge';
import { LanguageContext } from './providers/LanguageProvider';
import { Helmet } from 'react-helmet';
import AOS from 'aos';
import 'aos/dist/aos.css';
AOS.init({
  delay: 100,
  duration: 500,
  easing: 'ease-in-out',
  offset: 80,
  once: true,
});
import Cookie from './components/Cookie';
import ActivityIndicator from './squiblite/components/activityIndicator';
import { getQrCode } from './squiblite/services/firebase';

const Upload = lazy(() => import('./squiblite/pages/upload'));
const EmbeddedChallenge = lazy(() => import('./squiblite/pages/embeddedChallenge'));
const ChallengeComponent = lazy(() => import('./squiblite/pages/challenge'));
const Page = lazy(() => import('./components/Page'));

const App = (): JSX.Element => {
  const userLanguage = useContext(LanguageContext);
  const [result, setResult] = useState(null);
  const [path, updatePath] = useState<string>('');
  const [show, setShow] = useState(true);
  const [pageTitle, setPageTitle] = useState<string>('');
  const [pageDesc, setPageDesc] = useState<string>('');
  const [pageOGTitle, setPageOGTitle] = useState<string>('');
  const [pageOGDesc, setPageOGDesc] = useState<string>('');
  const [environment, setEnvironment] = useState<string>('');
  const [pageOGImg, setPageOGImg] = useState<string>('');
  const [pageOGType, setPageOGType] = useState<string>('');
  const [pageOGAuthor, setPageOGAuthor] = useState<string>('');
  const [pageOGPublished_date, setpageOGPublished_date] = useState<string>('');
  const [redirect, setRedirect] = useState<boolean>(false);

  const checkForCookie = () => {
    if (document.cookie.indexOf('accepted_cookies=') != -1) {
      setShow(false);
    }
  };

  const resetContent = () => {
    setResult(null);
  };

  const fallbackComponent = (
    <div style={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <p>Loading...</p>
    </div>
  );

  const origin = window.location.origin;
  const location = window.location.pathname;

  const isVotingPage = location.substring(0, 3) == '/v/' || location.substring(3, 6) == '/v/';
  const isEmbeddedVotingPage = location.substring(0, 12) == '/embedded/v/' || location.substring(3, 15) == '/embedded/v/';
  const isCreateVotingPage = location == '/voting' || location.substring(3, 10) == '/voting';
  const isQrPage = location.substring(0, 4) == '/qr/' || location.substring(3, 7) == '/qr/';
  const isMomask = location == '/momask';
  const isShirtQr = location == '/squibtshirt';

  if (isMomask) window.location.replace('v/7G72Kef9o0');

  if (isShirtQr) {
    const shirtVoting = ['v/sMp_iNrQ1y', 'v/fLdnmn3BmH', 'v/UVXPIS9Zk5', 'v/ErJae2CNPR', 'v/ffoGpuLgbq'];
    // redirect to random voting
    window.location.replace(shirtVoting[Math.floor(Math.random() * shirtVoting.length)]);
  }

  if (isQrPage) {
    const qrid = location.substring(0, 4) == '/qr/' ? location.substring(4) : location.substring(7);
    console.log(qrid);
    getQrCode(qrid)
      .then(result => {
        window.location.replace(result ? `../v/${result}` : '../home');
      })
      .catch(e => {
        console.error(e);
        window.location.replace('../home');
      });
  }

  // Needed for Language Switcher
  // ==============================================================================================
  const langPath = window.location.pathname.slice(1, 3);
  const realPath = window.location.pathname.slice(3);

  useEffect(() => {
    if (origin === 'https://www.squib.app') {
      setEnvironment('published');
    } else {
      setEnvironment('draft');
    }
  }, []);

  useEffect(() => {
    async function getInitialProps() {
      // Language Switcher
      // ==============================================================================================
      // let res;
      // console.log('Location', location);
      // if (langPath == 'en' && langPath != userLanguage.lang) {
      //   res = await getStoryblokAPI(`cdn/stories/${realPath}`, { version: environment });
      // } else if (langPath != 'en' && userLanguage.lang == 'en') {
      //   res = await getStoryblokAPI(`cdn/stories/en${location}`, { version: environment });
      // } else if (location == '/' || location == '/en') {
      //   res = await getStoryblokAPI(`cdn/stories${location}/home`, { version: environment });
      // } else {
      //   res = await getStoryblokAPI(`cdn/stories${location}`, { version: environment });
      // }
      // =============================================================================================
      let res;
      if (langPath == 'en' && langPath != userLanguage.lang) {
        res = await getStoryblokAPI(`cdn/stories/${realPath}`, { version: environment });
      } else if (langPath != 'en' && userLanguage.lang == 'en') {
        res = await getStoryblokAPI(`cdn/stories/en${location}`, { version: environment });
      } else if (location == '/' || location == '/en') {
        res = await getStoryblokAPI(`cdn/stories${location}/home`, { version: environment });
      } else if (location == '/blog') {
        res = await getStoryblokAPI('cdn/stories', {
          version: environment,
          starts_with: 'blog/',
        });
        setResult(res.data);
      } else {
        try {
          if (!isVotingPage && !isCreateVotingPage && !isEmbeddedVotingPage && !isQrPage) {
            res = await getStoryblokAPI(`cdn/stories${location}`, { version: environment });
          }
        } catch {
          res = { data: { story: { content: null } } };
          setResult(null);
          setRedirect(true);
        }
      }
      if (res && res.data.story) {
        if (res.data.story.content) {
          setResult(res.data.story.content);
          setPageTitle(res.data.story.content.metadata.title);
          setPageDesc(res.data.story.content.metadata.description);
          setPageOGTitle(res.data.story.content.metadata.og_title);
          setPageOGDesc(res.data.story.content.metadata.og_description);
          setPageOGImg(res.data.story.content.metadata.og_image);
          if (res.data.story.content.component === 'post') {
            setPageOGType('article');
            setPageOGAuthor(res.data.story.content.author[0].author_name);
            setpageOGPublished_date(res.data.story.content.author[0].date);
          }
        }
      } else if (res && res.data.stories) {
        setPageTitle(res.data.stories[0].content.metadata.title);
        setPageDesc(res.data.stories[0].content.metadata.description);
        setPageOGTitle(res.data.stories[0].content.metadata.og_title);
        setPageOGDesc(res.data.stories[0].content.metadata.og_description);
      }
      window.scrollTo(0, 0);
    }
    resetContent();
    getInitialProps();
    checkForCookie();
  }, [userLanguage.lang, path]);

  initEditor();
  window.storyblok.on('input', payload => {
    if (window.storyblok.isInEditor() && payload?.story.content !== result) {
      setResult(payload?.story.content);
    }
  });

  return (
    <Router>
      <div>
        <Helmet>
          <title>{result && pageTitle ? pageTitle : ''}</title>
          <meta property='og:type' content={result && pageOGType ? pageOGType : ''} />
          <meta name='description' content={result && pageDesc ? pageDesc : ''} />
          <meta name='author' content={result && pageOGAuthor ? pageOGAuthor : ''} />
          <meta name='publish_date' content={result && pageOGPublished_date ? pageOGPublished_date : ''} />
          <meta property='og:title' content={result && pageOGTitle ? pageOGTitle : ''} />
          <meta property='og:description' content={result && pageOGDesc ? pageOGDesc : ''} />
          <meta property='og:image' content={result && pageOGImg ? pageOGImg : ''} />
          <meta property='og:image:secure_url' content={result && pageOGImg ? pageOGImg : ''} />
          <meta property='og:image:width' content='1920' />
          <meta property='og:image:height' content='1280' />
          <meta property='twitter:card' content='summary' />
          <meta property='twitter:image' content={result && pageOGImg ? pageOGImg : ''} />
          <meta property='twitter:description' content={result && pageOGDesc ? pageOGDesc : ''} />
          <meta property='og:url' content={result && pageOGImg ? `https://www.squib.app${location}` : ''} />
        </Helmet>
        {!isEmbeddedVotingPage ? (
          <Layout updatePath={updatePath}>
            {result ? (
              <Suspense fallback={fallbackComponent}>
                <Page content={result} />
              </Suspense>
            ) : isCreateVotingPage ? (
              <Suspense fallback={fallbackComponent}>
                <Upload />
              </Suspense>
            ) : isVotingPage ? (
              <Suspense fallback={fallbackComponent}>
                <ChallengeComponent />
              </Suspense>
            ) : isQrPage ? (
              <Suspense fallback={fallbackComponent}>
                <div style={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }} />
                <ActivityIndicator />
              </Suspense>
            ) : (
              <div style={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <p>Loading...</p>
              </div>
            )}
            {show ? <Cookie /> : ''}
          </Layout>
        ) : (
          <Suspense fallback={fallbackComponent}>
            <EmbeddedChallenge />
          </Suspense>
        )}
        {/* Language Switcher ============================================================================================== */}
        {langPath == 'en' && langPath != userLanguage.lang ? <Redirect to={`${realPath}`} /> : <Redirect to={`${location}`} />}
        {langPath != 'en' && userLanguage.lang == 'en' ? <Redirect to={`en${location}`} /> : <Redirect to={`${location}`} />}
        <Route exact path='/en'>
          <Redirect to='/en/home' />
        </Route>
        <Route exact path='/'>
          <Redirect to='/home' />
        </Route>
        {redirect && <Redirect to='/home' />}
      </div>
    </Router>
  );
};

export default App;
